/**
 * BoardSession — client/core sync engine for one open board (docs/08 §1:
 * core/ owns "grid engine, sync provider, presence, soft-lock/awareness
 * helpers, undo manager"; feature code goes through this class and never
 * touches Yjs internals).
 *
 * Responsibilities:
 *  - HocuspocusProvider (v4) over ONE multiplexed WebSocket to /collab,
 *    document name "board:<boardId>", dev identity as ?user= + token.
 *  - Zustand store exposing connection status, active sheet, structure
 *    version, presence peers, and the F-45 offline freeze.
 *  - All mutations funnel through @kola/shared BoardDoc accessors inside
 *    transactions tagged with LOCAL_ORIGIN (F-12) so Y.UndoManager's
 *    trackedOrigins scopes undo to this user's edits (F-43, docs/07 C6/C8).
 *  - Awareness (F-41/F-42): user, selection, editingCell — ephemeral only,
 *    never persisted; soft locks have NO server-side state (docs/07 C7).
 *
 * This module is the ONE sanctioned place in the client that imports yjs
 * directly (UndoManager + observers); everything else uses BoardDoc.
 */
import { HocuspocusProvider, HocuspocusProviderWebsocket, WebSocketStatus } from "@hocuspocus/provider";
import {
  applyTextTypeSwitch,
  applyMarkRange,
  BoardDoc,
  type ColumnType,
  clipRangeOf,
  withClipRange,
  cellKey,
  clearMarkRange,
  getCells,
  getColMeta,
  getComments,
  getConnectors,
  getDesign,
  getDesignRefs,
  getDesignShots,
  getThumbPortraits,
  getThumbs,
  getThumbShots,
  getElements,
  getRowMeta,
  getSheets,
  parseCellPropKey,
  rangeFullyMarked,
  TEXT_MARK_KINDS,
  type Anchor,
  type ClipRange,
  type ArtDirection,
  type ArtDirectionTextField,
  type CellAddress,
  type CellRangeSelection,
  type CellScalar,
  type CellTextType,
  type CommentMessage,
  type ConnectorEndpoint,
  type DrawingElementPayload,
  type ConnectorSide,
  type ElementGeometryPatch,
  type ElementPayload,
  type ElementType,
  type FileElementPayload,
  type GrabbedElement,
  type ImageElementPayload,
  type LinkCardElementPayload,
  type PaletteSwatch,
  type PresenceUser,
  type TextMarkKind,
  type ThumbShotInfo,
} from "@kola/shared";
import * as Y from "yjs";
import { createStore, type StoreApi } from "zustand/vanilla";

import { debugLog, setDebugConnection } from "./debug-log.js";
import { getHost, wsUrl } from "./host.js";
import { serverInfoStore } from "./server-info.js";

import { showToast } from "../ui/toast.js";
import { applyCellCopies, type CellCopyWrite } from "./cell-copy.js";
import { everyCellFullyMarked, wholeTextMarks, type CellMarkTarget } from "./cell-marks.js";
import { withOpacity } from "./color.js";
import { newId } from "./ids.js";
import { createUndoGrouper } from "./undo-group.js";

/**
 * Transaction origin for every local user edit (F-12). Identity matters:
 * Y.UndoManager trackedOrigins compares by reference, so this is the single
 * shared instance for the whole client.
 */
export const LOCAL_ORIGIN: unknown = Object.freeze({ source: "local-user" });

/**
 * Transaction origin for AUTOMATIC comment-anchor degradation (docs/07 C4,
 * docs/04 §3: target deleted → reanchor to board + mark orphaned).
 * Deliberately NOT `LOCAL_ORIGIN` and NOT in the UndoManager's
 * `trackedOrigins`: degradation is a system consequence of someone's
 * deletion, not an edit this user made — F-43's "Ctrl+Z undoes only your own
 * operations" must not let a keypress re-attach an anchor to a target that
 * no longer exists (and every client that observes the deletion races the
 * same idempotent write, so "whose undo stack" has no meaningful answer).
 */
const DEGRADE_ORIGIN: unknown = Object.freeze({ source: "comment-degrade" });

/**
 * Transaction origin for AUTOMATIC contained-element anchor degradation
 * (docs/07 C4, docs/04 §2: a cell-anchored element whose row/column was
 * deleted degrades to a board anchor at its last resolved position — the F-36
 * analog of comment degradation). Like DEGRADE_ORIGIN it is a system
 * consequence of someone else's deletion, NOT this user's edit: kept off the
 * UndoManager's trackedOrigins so Ctrl+Z never re-pins an element to a cell
 * that no longer exists, and every client observing the deletion races the
 * same idempotent LWW write.
 */
const ELEMENT_DEGRADE_ORIGIN: unknown = Object.freeze({ source: "element-degrade" });

/**
 * Transaction origin for the empty-thread SWEEP (shared-layer
 * `deleteThreadIfEmpty`): two replicas concurrently deleting the last two
 * messages of a thread each keep it locally, and the merge converges to a
 * permanently EMPTY thread no UI path could otherwise remove. Like
 * DEGRADE_ORIGIN this is a system consequence of the merge, not a user edit —
 * never on the undo stack, idempotent across racing clients.
 */
const SWEEP_ORIGIN: unknown = Object.freeze({ source: "comment-sweep" });

/**
 * Transaction origin for the ASYNCHRONOUS link-unfurl payload patch (F-31,
 * docs/04 §7): the skeleton link-card element (created with LOCAL_ORIGIN on
 * paste) is later patched loading→ready/error when the `/unfurl` fetch resolves.
 * The string `"unfurl"` is deliberately NOT `LOCAL_ORIGIN` and NOT in the
 * UndoManager's `trackedOrigins` (which holds only LOCAL_ORIGIN), so the async
 * patch never enters or pollutes the user's undo stack — Ctrl+Z after a paste
 * undoes the whole card creation, not a half-applied metadata patch. It writes
 * only the element's `payload` key, so being treated as a "foreign" origin has
 * no effect beyond the elementsVersion bump (it touches no cells/rows/comments).
 */
const UNFURL_ORIGIN: unknown = "unfurl";

/**
 * Transaction origin for a column the SYSTEM adds because the board needs it
 * — F-126's BGM column on a sheet that was divided into sections before music
 * slots existed (features/bgm/ensure-column.ts). NOT `LOCAL_ORIGIN` and NOT
 * tracked by the UndoManager: the user did not add this column, so their
 * Ctrl+Z must not silently remove it (and then have it come straight back).
 */
const SYSTEM_COLUMN_ORIGIN: unknown = "system-column";

/**
 * Transaction origin for the F-38 connector sweep (docs/07 C4): deleting a
 * line left dangling by someone else's element delete. Like the element
 * degradation origin it is deliberately NOT `LOCAL_ORIGIN` and NOT tracked by
 * the UndoManager — it is a system consequence of another user's edit, and
 * Ctrl+Z must never "restore" a line into a hole.
 */
const CONNECTOR_SWEEP_ORIGIN: unknown = "connector-sweep";

/**
 * Raw WS close code the server uses when a newer deploy epoch supersedes this
 * instance (packages/server/src/core/epoch.ts EPOCH_SUPERSEDED_CLOSE).
 * Clients must reconnect immediately instead of backing off.
 */
export const EPOCH_SUPERSEDED_CODE = 4001;

/** F-45: edits are blocked after this long without a connection. */
export const OFFLINE_FREEZE_MS = 60_000;

export type ConnectionStatus = "connecting" | "connected" | "disconnected";

/** A remote client's presence, derived from awareness (never persisted). */
export interface RemotePeer {
  clientId: number;
  user: PresenceUser;
  selection?: CellRangeSelection;
  /** F-42 soft-lock claim. */
  editingCell?: CellAddress;
  /** F-46 element grab claim (awareness soft lock — docs/07 C9). */
  grabbedElement?: GrabbedElement;
}

export interface BoardSessionState {
  status: ConnectionStatus;
  /** First sync handshake with the server completed. */
  synced: boolean;
  /** F-45: offline > 60 s → grid becomes read-only until reconnect. */
  frozen: boolean;
  /** Permission-denied reason from onAuthenticate (board-not-found, …). */
  authError: string | null;
  /** Active sheet id (null until the doc is loaded/bootstrapped). */
  sheetId: string | null;
  /** Bumped on any row/column/sheet metadata change → re-derive layout. */
  structureVersion: number;
  /** Bumped on any floating-element change → re-derive the overlay. */
  elementsVersion: number;
  /** Bumped on any connector change → re-derive the connector layer (F-38). */
  connectorsVersion: number;
  /** Bumped on any comment-thread change → re-derive pins/panel (F-50..55). */
  commentsVersion: number;
  /** Bumped on any design-theme change → re-derive the panel + board band. */
  designVersion: number;
  /** Bumped on any thumbnail change (words, portraits, shots, run) → re-derive the panel. */
  thumbsVersion: number;
  /** This user's own grid selection (paste-image target, F-30/F-16). */
  localSelection: CellRangeSelection | null;
  /**
   * This user's OWN in-flight element grab (F-46), mirroring what
   * setGrabbedElement streams to peers via awareness. Peers see the drag via
   * `peers[*].grabbedElement`; the local client is excluded from `peers`, so
   * anything that must ride the live motion (comment pins anchored to the
   * dragged element — F-52 "pin moves with it", docs/04 §3 "follows drags
   * live") reads its own preview rect from here.
   */
  localGrab: GrabbedElement | null;
  peers: RemotePeer[];
}

type CellsChangedListener = (cellKeys: readonly string[]) => void;

export interface PasteWrite {
  rowId: string;
  colId: string;
  value: CellScalar;
}

/**
 * One element's share of a drag commit (F-32/F-34) — produced by the pure
 * planner in `features/elements/move-plan.ts` and applied by
 * `applyElementMoves`. Split this way so the "which keys did this gesture
 * actually change" decision (C2 — writing an unchanged key reverts a
 * concurrent peer) is testable without a DOM, and so a group drag and a single
 * drag go through exactly the same rule.
 */
export interface ElementMovePlan {
  elementId: string;
  /** New whole-object anchor, or absent when it must NOT be re-written. */
  anchor?: Anchor;
  /** ONLY the geometry keys this gesture changed (C2). */
  patch: ElementGeometryPatch;
  /** Cells to re-fit afterwards (F-26/F-36) — source, destination, or both. */
  fitCells: Array<{ rowId: string; colId: string }>;
}

/** One element to create as a copy (F-34 paste). `ref` is the ORIGINAL id. */
export interface ElementDuplicate {
  ref: string;
  type: ElementType;
  x: number;
  y: number;
  w: number;
  h: number;
  rotation: number;
  anchor: Anchor;
  payload: ElementPayload;
}

/** A copied connector, in terms of the duplicates' `ref`s (F-34 paste). */
export interface ConnectorDuplicate {
  from: { ref: string; side: ConnectorSide };
  to: { ref: string; side: ConnectorSide };
}

/**
 * What one undo-stack item touched — recorded at capture time from the
 * transaction's events (via the UndoManager 'stack-item-added/updated'
 * events) and consulted by the C6/C8 undo guards before applying the item.
 */
interface UndoItemInfo {
  /** Cell storage keys ("rowId:colId:prop") the item's transactions wrote. */
  cellPropKeys: Set<string>;
  /** Row ids the item's transactions CREATED (insert row / split). */
  createdRowIds: Set<string>;
  /** Column ids the item's transactions CREATED. */
  createdColIds: Set<string>;
}

/**
 * Comment-thread ids an undo-stack item CREATED — the C6 analog for threads
 * (see undo()): undoing your own createThread deletes the whole nested
 * subtree, including replies other users added since. Kept in a separate
 * SESSION-LIFETIME map (not UndoItemInfo, which is reset per active sheet):
 * comments are board-scoped like elements, and thread creations made before
 * the first sheet resolves must keep their guard through setActiveSheet.
 */
type CreatedThreadIds = Set<string>;

/** Structural view of yjs's StackItemEvent (the type is not re-exported). */
interface StackItemEventLike {
  stackItem: object;
  origin: unknown;
  type: "undo" | "redo";
  // Key typed `unknown` so lookups accept our concrete Y.Map instances;
  // the values mirror yjs's own StackItemEvent typing.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  changedParentTypes: Map<unknown, Array<Y.YEvent<any>>>;
}

export class BoardSession {
  readonly boardId: string;
  readonly board: BoardDoc;
  readonly user: PresenceUser;
  readonly store: StoreApi<BoardSessionState>;

  private readonly provider: HocuspocusProvider;
  private readonly socket: HocuspocusProviderWebsocket;
  private readonly undoManager: Y.UndoManager;
  private offlineTimer: number | null = null;
  private destroyed = false;
  /** F-90 undo grouping (see `beginUndoGroup`); built in the constructor. */
  private readonly undoGroup: () => () => void;

  private readonly cellListeners = new Set<CellsChangedListener>();
  private detachSheetObservers: (() => void) | null = null;
  private readonly sheetsObserver = () => this.refreshSheets();
  private readonly elementsObserver = () => this.bumpElements();
  private readonly connectorsObserver = () => this.bumpConnectors();
  private readonly designObserver = () => this.bumpDesign();
  /** Refs and shots live in their own roots — same version counter. */
  private readonly designCollectionsObserver = () => this.bumpDesign();
  private readonly thumbsObserver = () => this.bumpThumbs();
  /** Portraits and shots live in their own roots — same version counter. */
  private readonly thumbsCollectionsObserver = () => this.bumpThumbs();
  private readonly commentsObserver = (
    events: Y.YEvent<Y.Map<unknown>>[],
    transaction: Y.Transaction,
  ) => {
    this.trackForeignCommentEdits(events, transaction);
    this.bumpComments();
  };

  /* C6/C8 undo-guard state (docs/07; CLAUDE.md invariant 4) — per active
   * sheet, reset by setActiveSheet. */
  /** Cell storage keys whose LAST writer was a foreign client. */
  private foreignCellWrites = new Set<string>();
  /** Row ids any foreign client has edited (cells or row meta) since open. */
  private foreignTouchedRowIds = new Set<string>();
  /** Column ids any foreign client has edited since open. */
  private foreignTouchedColIds = new Set<string>();
  /** Per-undo-stack-item record of what the item touched. */
  private undoItemInfo = new WeakMap<object, UndoItemInfo>();

  /* C6-for-threads undo-guard state — SESSION-lifetime (comments are
   * board-scoped, not per-sheet; deliberately NOT reset by setActiveSheet). */
  /** Thread ids a foreign client has replied in / resolved since open. */
  private readonly foreignTouchedThreadIds = new Set<string>();
  /** Per-undo-stack-item record of the thread ids the item created. */
  private readonly commentUndoItemInfo = new WeakMap<object, CreatedThreadIds>();

  constructor(boardId: string, user: PresenceUser) {
    this.boardId = boardId;
    this.user = user;
    this.board = new BoardDoc(new Y.Doc());

    this.store = createStore<BoardSessionState>(() => ({
      status: "connecting",
      synced: false,
      frozen: false,
      authError: null,
      sheetId: null,
      structureVersion: 0,
      elementsVersion: 0,
      connectorsVersion: 0,
      commentsVersion: 0,
      designVersion: 0,
      thumbsVersion: 0,
      localSelection: null,
      localGrab: null,
      peers: [],
    }));

    // F-45: the freeze clock starts immediately; a successful connect clears it.
    this.startOfflineTimer();

    getSheets(this.board.doc).observeDeep(this.sheetsObserver);
    getElements(this.board.doc).observeDeep(this.elementsObserver);
    getConnectors(this.board.doc).observeDeep(this.connectorsObserver);
    getComments(this.board.doc).observeDeep(this.commentsObserver);
    getDesign(this.board.doc).observe(this.designObserver);
    getDesignRefs(this.board.doc).observeDeep(this.designCollectionsObserver);
    getDesignShots(this.board.doc).observeDeep(this.designCollectionsObserver);
    getThumbs(this.board.doc).observe(this.thumbsObserver);
    getThumbPortraits(this.board.doc).observeDeep(this.thumbsCollectionsObserver);
    getThumbShots(this.board.doc).observeDeep(this.thumbsCollectionsObserver);

    // F-43: per-user undo, created for the WHOLE session — not lazily on the
    // first setActiveSheet. The elements layer is interactive while the grid
    // still shows "Loading board…" (sheetId null), and a floating-element
    // write made in that pre-sync window must be capturable by Ctrl+Z; a
    // manager created only once a sheet id exists would leave those
    // transactions permanently outside the undo scope (C6/C8 machinery).
    // Per-sheet grid types join this scope in setActiveSheet via addToScope.
    // trackedOrigins scopes history to OUR edits; remote transactions are
    // never undone by this client. Comments join the scope for F-43: comment
    // ops are plain undoable edits — but only LOCAL_ORIGIN ones; the
    // automatic C4 degradation/sweep writes use DEGRADE_ORIGIN/SWEEP_ORIGIN
    // and stay OFF the undo stack. Undoing a thread CREATION is structural in
    // the C6 sense (it deletes the nested subtree, replies included), so the
    // recorder below stamps created thread ids per stack item and undo()
    // blocks once the thread gained foreign replies/resolves.
    this.undoManager = new Y.UndoManager(
      [
        getElements(this.board.doc),
        // F-38: connectors share the element scope so deleting an element and
        // cascading its lines — one transaction — is also ONE undo step.
        getConnectors(this.board.doc),
        getComments(this.board.doc),
        getDesign(this.board.doc),
        // F-130: the thumbnail words and picks are ordinary undoable edits.
        getThumbs(this.board.doc),
      ],
      {
        trackedOrigins: new Set([LOCAL_ORIGIN]),
        captureTimeout: 300,
      },
    );

    // C6-for-threads recorder — SESSION-lifetime (unlike the per-sheet
    // recorder in setActiveSheet): stamp every undo stack item with the
    // thread ids its transactions CREATED, so undo() can block deleting a
    // thread someone else has since engaged with. 'stack-item-updated' fires
    // when a transaction merges into the previous item (captureTimeout) —
    // union into the same record.
    const comments = getComments(this.board.doc);
    const recordCommentItemInfo = (event: StackItemEventLike) => {
      if (event.type !== "undo") return; // redo items are yjs-guarded
      for (const commentEvent of event.changedParentTypes.get(comments) ?? []) {
        if (commentEvent.path.length !== 0) continue;
        for (const [key, change] of commentEvent.changes.keys) {
          if (change.action !== "add") continue;
          let created = this.commentUndoItemInfo.get(event.stackItem);
          if (!created) {
            created = new Set();
            this.commentUndoItemInfo.set(event.stackItem, created);
          }
          created.add(key);
        }
      }
    };
    this.undoManager.on("stack-item-added", recordCommentItemInfo);
    this.undoManager.on("stack-item-updated", recordCommentItemInfo);

    this.undoGroup = createUndoGrouper(this.undoManager);

    // Dev identity rides the WS URL query (?user=…); the Vite proxy forwards
    // /collab (ws) to the server. `user.id` is the CANONICAL id (it is also
    // what lands in the doc as a comment's authorId), so it is percent-encoded
    // HERE, at the transport — URLSearchParams decodes it back server-side.
    // The URL itself comes from the shell (core/host.ts): same-origin in the
    // web shell, the Kola origin when embedded in the desktop app.
    const url = `${wsUrl("/collab")}?user=${encodeURIComponent(user.id)}`;

    // Explicit socket: the provider then registers its callbacks exactly once
    // (via attach()) instead of double-registering them on a self-managed
    // socket. v4 multiplexes all documents over this one WebSocket.
    this.socket = new HocuspocusProviderWebsocket({ url });
    this.provider = new HocuspocusProvider({
      websocketProvider: this.socket,
      name: `board:${boardId}`,
      document: this.board.doc,
      // v4 requires an Auth message before establishing a document, and
      // re-reads a function token on EVERY socket open (its onOpen →
      // sendToken → getToken). Web shells send any non-empty token — the
      // kola_session cookie (firebase) or ?user= (dev) is the credential.
      // The embedded shell has no cookie on the Kola origin, so it sends the
      // Firebase ID token itself — the server's third auth path — and the
      // per-open re-read is what keeps an hourly-expiring token valid across
      // Cloud Run's ≤60 min socket recycles and every reconnect.
      // (The auth mode is read from core/server-info.ts, not core/api.ts:
      // the server's e2e harness type-checks this file, and api.ts carries
      // Vite's `import.meta.env`, which that tsconfig does not know.)
      token: async () => {
        const host = getHost();
        const mode = serverInfoStore.getState().mode;
        if (host !== null && (mode === "firebase" || mode === "product")) {
          return (await host.getIdToken()) ?? "";
        }
        return encodeURIComponent(user.id);
      },
      onStatus: ({ status }) => this.handleStatus(status),
      onSynced: ({ state }) => this.handleSynced(state),
      onClose: ({ event }) => {
        // Debug log: the close code is the one fact a "Connection lost" banner
        // hides — 4001 is a deploy, 1006 with no reason is a refused or
        // dropped upgrade (a 503 from a draining instance looks exactly so).
        if (event.code === EPOCH_SUPERSEDED_CODE) {
          debugLog.info("socket", "Server redeploying — reconnecting immediately (close 4001)");
        } else if (event.code !== 1000 && event.code !== 1001) {
          debugLog.warn(
            "socket",
            `Collaboration socket closed (code ${event.code}${event.reason ? `: ${event.reason}` : ""})`,
          );
        }
        // Epoch supersession (deploy safety, docs/06 §2.1): the old server
        // instance flushed and closed us on purpose — reconnect immediately,
        // skipping the usual backoff.
        if (event.code === EPOCH_SUPERSEDED_CODE && !this.destroyed) {
          queueMicrotask(() => {
            if (!this.destroyed) void this.socket.connect();
          });
        }
      },
      onAuthenticationFailed: ({ reason }) => {
        debugLog.error("socket", `Board refused by the server: ${reason}`);
        this.store.setState({ authError: reason });
      },
      onAwarenessChange: ({ states }) => this.handleAwareness(states),
    });
    this.provider.attach();

    // Publish who we are (F-41). Awareness renews itself; a crashed client's
    // state — including any editing claim — expires with its connection.
    this.provider.setAwarenessField("user", this.user);
  }

  /* ------------------------------------------------------------------ */
  /* Presence (F-41 / F-42)                                              */
  /* ------------------------------------------------------------------ */

  setSelection(selection: CellRangeSelection | undefined): void {
    if (this.destroyed) return;
    this.provider.setAwarenessField("selection", selection ?? null);
    this.store.setState({ localSelection: selection ?? null });
  }

  /** F-42 soft lock: claim (or clear) "I am editing this cell". */
  setEditingCell(cell: CellAddress | undefined): void {
    if (this.destroyed) return;
    this.provider.setAwarenessField("editingCell", cell ?? null);
  }

  /**
   * F-46 grab indicator: claim (or clear) "I am dragging/resizing this
   * element". Awareness only — the claim dies with the connection (C7/C9).
   * During the drag the claim carries the live preview rect (C9: positions
   * are awareness-streamed while dragging; the CRDT commit happens once on
   * release).
   */
  setGrabbedElement(grab: GrabbedElement | undefined): void {
    if (this.destroyed) return;
    this.provider.setAwarenessField("grabbedElement", grab ?? null);
    // Mirror the claim locally: awareness excludes self from `peers`, but the
    // local client's own overlays (comment pins on the dragged element, F-52)
    // must ride the same live rect remote clients see (docs/04 §3).
    this.store.setState({ localGrab: grab ?? null });
  }

  /* ------------------------------------------------------------------ */
  /* Undo (F-43)                                                         */
  /* ------------------------------------------------------------------ */

  /**
   * Per-user undo with the two docs/07 guards (CLAUDE.md invariant 4):
   *
   *  - C6: undo of a STRUCTURAL op is BLOCKED with a notice when the
   *    rows/columns it created have since been edited by someone else —
   *    undoing would remove the container and orphan their content. Cell
   *    history (F-81) is the recovery path, so the block is permanent.
   *  - C8: undo of a CELL VALUE is SKIPPED with a toast when a foreign client
   *    was the last writer of that key — instead of re-applying our stale
   *    value over their newer one. The skipped item is dropped and the next
   *    older item is considered in the same keypress.
   *
   * Also guarded by the F-45 offline freeze: while frozen, undo/redo must not
   * mutate the local doc (they would replay on reconnect, exactly the
   * unbounded divergence the freeze exists to prevent).
   */
  undo(): void {
    if (!this.canEdit()) return;
    const manager = this.undoManager;
    for (;;) {
      const top = manager.undoStack[manager.undoStack.length - 1] as object | undefined;
      if (top === undefined) return;
      // C6 (threads): block undoing a thread CREATION once someone else has
      // replied in (or resolved) the thread — the undo would delete the whole
      // nested subtree, destroying their messages on every replica; their own
      // undo could never restore an insert whose parent was deleted. Same
      // policy and permanence as the row/column block below.
      const createdThreads = this.commentUndoItemInfo.get(top);
      if (
        createdThreads !== undefined &&
        [...createdThreads].some((id) => this.foreignTouchedThreadIds.has(id))
      ) {
        showToast("Can't undo: someone else has replied in that comment thread");
        return;
      }
      const info = this.undoItemInfo.get(top);
      if (info) {
        // C6 — block structural undo after foreign edits to created rows/cols.
        const rowsBlocked = [...info.createdRowIds].some((id) =>
          this.foreignTouchedRowIds.has(id),
        );
        const colsBlocked = [...info.createdColIds].some((id) =>
          this.foreignTouchedColIds.has(id),
        );
        if (rowsBlocked || colsBlocked) {
          showToast(
            `Can't undo: someone else has edited the affected ${rowsBlocked ? "rows" : "columns"}`,
          );
          return;
        }
        // C8 — skip a value item whose key a foreign client wrote last.
        const foreignOverwrote = [...info.cellPropKeys].some((key) =>
          this.foreignCellWrites.has(key),
        );
        if (foreignOverwrote) {
          manager.undoStack.pop(); // consume without re-applying stale value
          showToast("Undo skipped: someone else edited that cell after you");
          continue;
        }
      }
      manager.undo();
      return;
    }
  }

  redo(): void {
    if (!this.canEdit()) return; // F-45: no doc mutations while frozen
    this.undoManager.redo();
  }

  /* ------------------------------------------------------------------ */
  /* Mutations — all through BoardDoc accessors with LOCAL_ORIGIN (F-12) */
  /* ------------------------------------------------------------------ */

  setCellValue(sheetId: string, rowId: string, colId: string, value: CellScalar): void {
    if (!this.canEdit()) return;
    // setCellValueAsHuman writes `v` AND clears `st` (F-70.5: any human edit
    // clears machine-translation status) — the rule lives in the shared
    // accessor layer so every human write path shares it.
    this.board.setCellValueAsHuman(sheetId, rowId, colId, value, LOCAL_ORIGIN);
  }

  /** F-16: one paste = ONE transaction = one sync unit = one undo step. */
  pasteCells(sheetId: string, writes: readonly PasteWrite[]): void {
    if (!this.canEdit() || writes.length === 0) return;
    this.board.transact(() => {
      for (const { rowId, colId, value } of writes) {
        // Same human-write path as single edits: also clears `st`, so pasted
        // human content can never be treated as machine-owned and overwritten
        // by a late translation batch (C5.1 / F-70.5).
        this.board.setCellValueAsHuman(sheetId, rowId, colId, value, LOCAL_ORIGIN);
      }
    }, LOCAL_ORIGIN);
  }

  /**
   * F-16 (2026-10): paste of a block copied FROM A KOLA GRID — each cell's
   * value, format, translation status, marks and line kinds, so it lands
   * exactly as it was copied (core/cell-copy.ts). One transaction = one sync
   * unit = one undo step, like `pasteCells`.
   */
  pasteCellCopies(sheetId: string, writes: readonly CellCopyWrite[]): void {
    if (!this.canEdit() || writes.length === 0) return;
    applyCellCopies(this.board, sheetId, writes, LOCAL_ORIGIN);
  }

  /**
   * Composite feature op: run several session mutations as ONE transaction =
   * one sync unit = one undo step (nested accessor transactions join the
   * outer one). Used by F-16 paste-with-growth and F-14 group resize/autofit.
   */
  batch(fn: () => void): void {
    if (!this.canEdit()) return;
    this.board.transact(fn, LOCAL_ORIGIN);
  }

  /**
   * Resolves once every local change has been acknowledged by the server —
   * for a REST call that must read what this client JUST wrote. F-70's
   * "translate into a new column" mints the column here and then asks the
   * server to plan the run against the doc it holds; without this wait the
   * plan can race the WebSocket update and 400 with unknown-column.
   *
   * Resolves at once when nothing is pending, and gives up after `timeoutMs`
   * rather than hang a dialog on a dead socket — the server's own validation
   * is the backstop either way.
   */
  whenFlushed(timeoutMs = 3000): Promise<void> {
    if (this.provider.unsyncedChanges === 0) return Promise.resolve();
    return new Promise((resolve) => {
      const done = () => {
        clearTimeout(timer);
        this.provider.off("unsyncedChanges", onChange);
        resolve();
      };
      const onChange = ({ number }: { number: number }) => {
        if (number === 0) done();
      };
      const timer = setTimeout(done, timeoutMs);
      this.provider.on("unsyncedChanges", onChange);
    });
  }

  /**
   * A NAMED column, appended at the end — the target "translate into a new
   * column" mints (F-70). Returns its stable id, or null while frozen.
   */
  insertNamedColumn(
    sheetId: string,
    name: string,
    options: { index?: number | undefined; type?: ColumnType | undefined } = {},
  ): string | null {
    if (!this.canEdit()) return null;
    const colId = `c${newId(10)}`;
    // `index`/`type` are the board agent's (F-144) additions: a plan may
    // place a column and type it. Omitted = appended, text — unchanged for
    // the translate caller.
    this.board.addColumn(sheetId, colId, {
      name,
      ...(options.index === undefined ? {} : { index: options.index }),
      ...(options.type === undefined ? {} : { type: options.type }),
      origin: LOCAL_ORIGIN,
    });
    return colId;
  }

  /**
   * Add a column the system decided the board needs (SYSTEM_COLUMN_ORIGIN —
   * off undo). Idempotent on `colId`: `addColumn` skips an id that already
   * exists, which is what lets every client mint the same deterministic id
   * for the same purpose and still converge on one column. Returns true when
   * the column was added by THIS call.
   */
  addSystemColumn(
    sheetId: string,
    colId: string,
    options: { name: string; index: number; width: number },
  ): boolean {
    if (!this.canEdit() || this.board.hasColumn(sheetId, colId)) return false;
    this.board.addColumn(sheetId, colId, { ...options, origin: SYSTEM_COLUMN_ORIGIN });
    return true;
  }

  /** Returns the created row's stable id, or null if editing is frozen. */
  insertRow(sheetId: string, index: number): string | null {
    if (!this.canEdit()) return null;
    const rowId = `r${newId(10)}`;
    this.board.addRow(sheetId, rowId, { index, origin: LOCAL_ORIGIN });
    return rowId;
  }

  /**
   * F-90 BULK insert: `count` rows at `index` in ONE pass, returning their
   * stable ids in order (empty while frozen).
   *
   * NOT `insertRow` in a loop: that is O(n²) — each call re-reads and re-sorts
   * the whole order to find its neighbors (measured 2026-07-16: 93 ms at 2k,
   * ~1 min at the 50k ceiling), which is exactly the freeze F-90 forbids. The
   * shared `addRows` accessor mints the whole batch's fractional ord keys in
   * one `generateNKeysBetween` call instead.
   *
   * The batch is ONE transaction (the accessor's own), so the caller gets one
   * sync unit per call — see `runScriptImport` for how a 10k-row import stays
   * one UNDO step across many such calls.
   */
  insertRows(sheetId: string, count: number, index?: number): string[] {
    if (!this.canEdit() || count <= 0) return [];
    const rowIds = Array.from({ length: count }, () => `r${newId(10)}`);
    // `index` omitted = append (the accessor's default) — what an import wants,
    // and it saves re-deriving the row count for every chunk.
    return this.board.addRows(sheetId, rowIds, {
      ...(index === undefined ? {} : { index }),
      origin: LOCAL_ORIGIN,
    });
  }

  /**
   * Group everything written until the returned `end()` into ONE undo-stack
   * item (F-90: "import is one undo step"), even across many transactions and
   * the `await`s between them.
   *
   * Yjs merges consecutive tracked transactions into the top stack item when
   * they land within `captureTimeout` (300 ms here) of each other. A chunked
   * import yields to the event loop between chunks, so the gaps are at the
   * mercy of whatever else the tab is doing — one slow frame and Ctrl+Z would
   * undo 500 of 10,000 rows, leaving the user to press it twenty times. Rather
   * than a giant transaction (which is the freeze) or a hopeful timeout, the
   * merge window is opened explicitly for the group's duration.
   *
   * The mechanism (including why overlapping groups must be COUNTED rather
   * than each saving their own timeout) lives in `core/undo-group.ts`, where it
   * is testable without a WebSocket.
   */
  beginUndoGroup(): () => void {
    return this.undoGroup();
  }

  /** Returns the created column's stable id, or null if editing is frozen. */
  insertColumn(sheetId: string, index: number): string | null {
    if (!this.canEdit()) return null;
    // Name left empty — the grid titles unnamed columns by position (A, B, …).
    const colId = `c${newId(10)}`;
    this.board.addColumn(sheetId, colId, { index, name: "", origin: LOCAL_ORIGIN });
    return colId;
  }

  deleteRow(sheetId: string, rowId: string): void {
    if (!this.canEdit()) return;
    this.board.deleteRow(sheetId, rowId, LOCAL_ORIGIN);
  }

  /** Multi-row-select "Delete N rows" — one transaction, one undo step. */
  deleteRows(sheetId: string, rowIds: readonly string[]): void {
    if (!this.canEdit() || rowIds.length === 0) return;
    this.board.deleteRows(sheetId, rowIds, LOCAL_ORIGIN);
  }

  deleteColumn(sheetId: string, colId: string): void {
    if (!this.canEdit()) return;
    this.board.deleteColumn(sheetId, colId, LOCAL_ORIGIN);
  }

  /** F-13 drag-move: a single fractional-ord write (docs/07 C1). */
  moveRow(sheetId: string, rowId: string, index: number): void {
    if (!this.canEdit()) return;
    this.board.moveRow(sheetId, rowId, index, LOCAL_ORIGIN);
  }

  moveColumn(sheetId: string, colId: string, index: number): void {
    if (!this.canEdit()) return;
    this.board.moveColumn(sheetId, colId, index, LOCAL_ORIGIN);
  }

  /**
   * F-23/F-12b: rename a column (the name the hover pill shows). One LWW `name`
   * write on the column's own key (C2 — merges with a concurrent width/ord
   * change). Returns false when the shared accessor refused it: the F-28 locked
   * "Original Script" column, or a column deleted concurrently (C4). The UI does
   * not offer rename on the locked column, so a false here is the race, not the
   * common path.
   */
  setColumnName(sheetId: string, colId: string, name: string): boolean {
    if (!this.canEdit()) return false;
    return this.board.setColumnName(sheetId, colId, name, LOCAL_ORIGIN);
  }

  /** F-14: commit once on resize end — one small CRDT op broadcast. */
  setColumnWidth(sheetId: string, colId: string, width: number): void {
    if (!this.canEdit()) return;
    this.board.setColumnWidth(sheetId, colId, Math.round(width), LOCAL_ORIGIN);
  }

  /** F-14: manual row height — a MINIMUM for F-26 auto-grow, one LWW write. */
  setRowHeight(sheetId: string, rowId: string, height: number): void {
    if (!this.canEdit()) return;
    this.board.setRowHeight(sheetId, rowId, Math.round(height), LOCAL_ORIGIN);
  }

  /**
   * F-12b/F-13 duplicate: insert a copy right after the source row, carrying
   * cell values, formats, and translation status. ONE transaction = one sync
   * unit = one undo step; the created row id is recorded by the C6 undo
   * machinery like any other insert.
   */
  duplicateRow(sheetId: string, rowId: string): string | null {
    if (!this.canEdit()) return null;
    const index = this.board.sortedRowIds(sheetId).indexOf(rowId);
    if (index < 0) return null;
    const newRowId = `r${newId(10)}`;
    this.board.transact(() => {
      const source = this.board.getRow(sheetId, rowId);
      this.board.addRow(sheetId, newRowId, {
        index: index + 1,
        ...(source !== undefined ? { height: source.height } : {}),
        origin: LOCAL_ORIGIN,
      });
      for (const colId of this.board.sortedColIds(sheetId)) {
        this.copyCell(sheetId, rowId, colId, newRowId, colId);
      }
      // F-26 (2026-07-14): cell-anchored elements "count as cell content", so a
      // row's contained images duplicate with it (onto the new row's cells).
      this.duplicateContainedElements(
        (anchor) => anchor.rowId === rowId,
        (anchor) => ({ ...anchor, rowId: newRowId }),
      );
    }, LOCAL_ORIGIN);
    return newRowId;
  }

  /** Same as duplicateRow, for columns (copy inserted to the right). */
  duplicateColumn(sheetId: string, colId: string): string | null {
    if (!this.canEdit()) return null;
    const index = this.board.sortedColIds(sheetId).indexOf(colId);
    if (index < 0) return null;
    const newColId = `c${newId(10)}`;
    this.board.transact(() => {
      const source = this.board.getColumn(sheetId, colId);
      this.board.addColumn(sheetId, newColId, {
        index: index + 1,
        name: source?.name ?? "",
        ...(source !== undefined
          ? { type: source.type, width: source.width, frozen: source.frozen }
          : {}),
        origin: LOCAL_ORIGIN,
      });
      for (const rowId of this.board.sortedRowIds(sheetId)) {
        this.copyCell(sheetId, rowId, colId, rowId, newColId);
      }
      // F-26: a column's contained elements duplicate with it (see duplicateRow).
      this.duplicateContainedElements(
        (anchor) => anchor.colId === colId,
        (anchor) => ({ ...anchor, colId: newColId }),
      );
    }, LOCAL_ORIGIN);
    return newColId;
  }

  /** Copy one cell's v/format/marks/st into a freshly-created target (duplicate ops). */
  private copyCell(
    sheetId: string,
    fromRow: string,
    fromCol: string,
    toRow: string,
    toCol: string,
  ): void {
    const snapshot = this.board.getCell(sheetId, fromRow, fromCol);
    if (snapshot === undefined) return;
    if (snapshot.v !== null) {
      this.board.setCellValue(sheetId, toRow, toCol, snapshot.v, LOCAL_ORIGIN);
    }
    if (snapshot.fmt !== undefined) {
      // Legacy-carried format props (fmt is the derived aggregate view; the
      // whole-object key is retired). The target cell is freshly created, so
      // the deprecated whole-replace alias cannot revert concurrent writes.
      this.board.setCellFormat(sheetId, toRow, toCol, snapshot.fmt, LOCAL_ORIGIN);
    }
    if (snapshot.bg !== undefined) {
      // F-17-bg: background copies through its own per-property setter (after
      // setCellFormat, whose whole-replace semantics would clear the bg key).
      this.board.setCellBg(sheetId, toRow, toCol, snapshot.bg, LOCAL_ORIGIN);
    }
    if (snapshot.st !== undefined) {
      this.board.setCellStatus(sheetId, toRow, toCol, snapshot.st, LOCAL_ORIGIN);
    }
    if (snapshot.marks !== undefined) {
      // F-17-marks: the ranges are offsets into text the copy now carries
      // verbatim, so they transfer as they stand. AFTER the value write, whose
      // own rebase pass would otherwise re-anchor them against the empty text
      // the target cell had a moment ago.
      for (const kind of TEXT_MARK_KINDS) {
        const marks = snapshot.marks[kind];
        if (marks === undefined) continue;
        this.board.setCellMarks(sheetId, toRow, toCol, kind, marks, LOCAL_ORIGIN);
      }
    }
    if (snapshot.ty !== undefined) {
      // F-17-type: same as the marks — offsets into text the copy carries
      // verbatim, written after the value for the same reason.
      this.board.setCellTextTypes(sheetId, toRow, toCol, snapshot.ty, LOCAL_ORIGIN);
    }
  }

  /**
   * Recreate every element CONTAINED (cell-anchored) in a source row/column
   * onto the duplicate track: a fresh element id, identical geometry/z/rotation
   * and payload, with its anchor re-pointed at the new row/column (`remap`).
   * Called INSIDE a duplicateRow/duplicateColumn transaction, so the copies
   * share its single sync unit / undo step. Board-anchored (free-floating)
   * elements are not row/column content and are left untouched.
   */
  private duplicateContainedElements(
    matches: (anchor: Extract<Anchor, { kind: "cell" }>) => boolean,
    remap: (anchor: Extract<Anchor, { kind: "cell" }>) => Anchor,
  ): void {
    for (const el of this.board.listElements()) {
      if (el.anchor.kind !== "cell" || !matches(el.anchor)) continue;
      this.board.addElement(
        `e${newId(10)}`,
        {
          type: el.type,
          x: el.x,
          y: el.y,
          w: el.w,
          h: el.h,
          z: el.z,
          rotation: el.rotation,
          anchor: remap(el.anchor),
          payload: el.payload,
          // The COPY is a new object this user just made, so authorship is
          // theirs (F-110) — not the original author's.
          by: this.user.id,
        },
        LOCAL_ORIGIN,
      );
    }
  }

  /**
   * F-27/F-17-bg: set (undefined = clear) the background fill of every given
   * cell in ONE transaction = one sync unit = one undo step. Each cell is a
   * single flat per-property `bg` write via the shared accessor (C2 — merges
   * with concurrent value edits and machine translation; bg never counts as
   * occupancy for C5.1). Deliberately NOT gated on F-42 soft-lock claims: a
   * fill can't destroy anyone's in-flight typing.
   */
  setCellsBg(
    sheetId: string,
    cells: readonly { rowId: string; colId: string }[],
    color: string | undefined,
  ): void {
    if (!this.canEdit() || cells.length === 0) return;
    this.board.transact(() => {
      for (const { rowId, colId } of cells) {
        this.board.setCellBg(sheetId, rowId, colId, color, LOCAL_ORIGIN);
      }
    }, LOCAL_ORIGIN);
  }

  /**
   * F-27/F-17-bg opacity: re-express every selected cell's EXISTING fill at
   * `pct` opacity, keeping each cell's own hue. One transaction = one undo
   * step, same as `setCellsBg`.
   *
   * Per-cell rather than "apply this one color at this opacity" so a mixed
   * selection stays mixed — dragging the slider over a red/blue range fades
   * both, it does not repaint them one color. Cells with no fill are skipped:
   * opacity is a property OF a fill, and inventing a transparent black for an
   * empty cell would be a fill the user never asked for.
   */
  setCellsBgOpacity(
    sheetId: string,
    cells: readonly { rowId: string; colId: string }[],
    pct: number,
  ): void {
    if (!this.canEdit() || cells.length === 0) return;
    this.board.transact(() => {
      for (const { rowId, colId } of cells) {
        const bg = this.board.getCellBg(sheetId, rowId, colId);
        if (bg === undefined) continue;
        const faded = withOpacity(bg, pct);
        // A foreign writer's non-hex value: leave it exactly as it is rather
        // than replacing something we cannot round-trip.
        if (faded === null || faded === bg) continue;
        this.board.setCellBg(sheetId, rowId, colId, faded, LOCAL_ORIGIN);
      }
    }, LOCAL_ORIGIN);
  }

  /**
   * F-27/F-17-color: set (undefined = back to automatic readable ink) the TEXT
   * color of every given cell, in ONE transaction = one undo step. Its own
   * flat per-property key per cell (C2), so it merges with a concurrent fill
   * or value edit — and, like `setCellsBg`, it is deliberately not gated on
   * F-42 soft-lock claims: recoloring cannot destroy in-flight typing.
   */
  setCellsColor(
    sheetId: string,
    cells: readonly { rowId: string; colId: string }[],
    color: string | undefined,
  ): void {
    if (!this.canEdit() || cells.length === 0) return;
    this.board.transact(() => {
      for (const { rowId, colId } of cells) {
        this.board.setCellColor(sheetId, rowId, colId, color, LOCAL_ORIGIN);
      }
    }, LOCAL_ORIGIN);
  }

  /**
   * F-17-marks: bold / strike-through over PART of a cell's text.
   *
   * Toggling, the way every editor does it: a selection that is already
   * entirely marked is unmarked, one that is only partly marked is marked
   * whole. The read and the write share one transaction so a peer's edit
   * cannot land between deciding and doing.
   *
   * Like the whole-cell format writers this is deliberately NOT gated on
   * F-42 soft-lock claims — marking text cannot destroy anyone's in-flight
   * typing, and the mark rides that typing via `rebaseTextMarks`.
   */
  toggleCellMark(
    sheetId: string,
    rowId: string,
    colId: string,
    kind: "bold" | "strike",
    start: number,
    end: number,
  ): void {
    if (!this.canEdit() || end <= start) return;
    this.board.transact(() => {
      const text = this.board.getCellText(sheetId, rowId, colId);
      if (text.length === 0) return;
      const marks = this.board.getCellMarks(sheetId, rowId, colId, kind);
      const next = rangeFullyMarked(text, marks, start, end)
        ? clearMarkRange(text, marks, start, end)
        : applyMarkRange(text, marks, start, end);
      this.board.setCellMarks(sheetId, rowId, colId, kind, next, LOCAL_ORIGIN);
    }, LOCAL_ORIGIN);
  }

  /**
   * F-17-marks: ink color (`color`) or highlight (`hl`) over part of a cell's
   * text. `undefined` clears the range rather than writing a color, which is
   * what "Auto" / "No highlight" do — the same "no explicit color" semantics
   * the whole-cell `setCellsColor` has, one range down.
   */
  setCellMarkColor(
    sheetId: string,
    rowId: string,
    colId: string,
    kind: "color" | "hl",
    start: number,
    end: number,
    color: string | undefined,
  ): void {
    if (!this.canEdit() || end <= start) return;
    this.board.transact(() => {
      const text = this.board.getCellText(sheetId, rowId, colId);
      if (text.length === 0) return;
      const marks = this.board.getCellMarks(sheetId, rowId, colId, kind);
      const next =
        color === undefined
          ? clearMarkRange(text, marks, start, end)
          : applyMarkRange(text, marks, start, end, color);
      this.board.setCellMarks(sheetId, rowId, colId, kind, next, LOCAL_ORIGIN);
    }, LOCAL_ORIGIN);
  }

  /**
   * F-17-type: switch the line kind at `at` in `text` — "from here on it is
   * sound" (`undefined` = back to narration). What picking a kind from the
   * `/` menu does, in either editing surface.
   *
   * `text` is the editor's draft with the `/query` already cut out, and it is
   * FLUSHED into the doc first, in the same transaction as the switch — the
   * grid's `flushEditorDraft` / the script view's `flushBlock` rule, for the
   * same reason: a switch is an offset into the cell's text, and there is no
   * such thing as a switch into text that has not landed yet. The value is
   * written only if it differs (an identical write would clear `st`, F-70.5,
   * and steal last-writer over a concurrent remote edit). Read-modify-write
   * of the one `ty` key, like the mark writers above.
   */
  setCellTextTypeAt(
    sheetId: string,
    rowId: string,
    colId: string,
    text: string,
    at: number,
    ty: CellTextType | undefined,
  ): void {
    if (!this.canEdit()) return;
    this.board.transact(() => {
      if (text !== this.board.getCellText(sheetId, rowId, colId)) {
        this.board.setCellValueAsHuman(
          sheetId,
          rowId,
          colId,
          text === "" ? null : text,
          LOCAL_ORIGIN,
        );
      }
      const current = this.board.getCellText(sheetId, rowId, colId);
      const switches = this.board.getCellTextTypes(sheetId, rowId, colId);
      this.board.setCellTextTypes(
        sheetId,
        rowId,
        colId,
        applyTextTypeSwitch(current, switches, at, ty),
        LOCAL_ORIGIN,
      );
    }, LOCAL_ORIGIN);
  }

  /**
   * F-27/F-17-marks: bold / strike-through over the WHOLE text of every
   * selected cell — what "select these cells and hit B" means in the grid,
   * where the gesture is a cell range and not a character range.
   *
   * It writes the SAME per-range marks the script view writes, over
   * `[0, text.length)` of each cell, rather than inventing a second whole-cell
   * property: a cell whose every character is bold IS a bold cell, both
   * renderers already paint it, and formatting half of it afterwards in either
   * view just subtracts from the range instead of fighting a separate flag.
   *
   * Toggling reads the selection AS A WHOLE: every cell with text already
   * fully marked → the press clears them all; anything short of that → the
   * press marks them all. A per-cell toggle would invert a mixed selection
   * into a differently-mixed one, which is never what anyone means.
   *
   * One transaction = one sync unit = one undo step. Cells with no text are
   * skipped — a mark with nothing to anchor to resolves to nothing. Like the
   * other format writers this is deliberately NOT gated on F-42 soft-lock
   * claims: marking text cannot destroy anyone's in-flight typing.
   */
  toggleCellsMark(
    sheetId: string,
    cells: readonly { rowId: string; colId: string }[],
    kind: "bold" | "strike",
  ): void {
    if (!this.canEdit() || cells.length === 0) return;
    this.board.transact(() => {
      const targets = [...this.markTargets(sheetId, cells, kind)].filter(
        (t) => t.text.length > 0,
      );
      if (targets.length === 0) return;
      // Read the whole selection FIRST, then write: the answer to "is this
      // already bold?" must be the same for every cell in one press.
      const on = !everyCellFullyMarked(targets);
      for (const target of targets) {
        this.board.setCellMarks(
          sheetId,
          target.rowId,
          target.colId,
          kind,
          wholeTextMarks(target, on),
          LOCAL_ORIGIN,
        );
      }
    }, LOCAL_ORIGIN);
  }

  /**
   * Is EVERY selected cell that has text already marked `kind` end to end?
   * What the F-27 toolbar's B / S buttons render as pressed — and, by
   * `toggleCellsMark`'s rule, exactly what the next press would clear.
   */
  cellsFullyMarked(
    sheetId: string,
    cells: readonly { rowId: string; colId: string }[],
    kind: "bold" | "strike",
  ): boolean {
    return everyCellFullyMarked(this.markTargets(sheetId, cells, kind));
  }

  /**
   * Each cell's text + its stored marks of one kind (../core/cell-marks.ts).
   *
   * LAZY, because `cellsFullyMarked` runs on every re-fold of the toolbar's
   * preview — i.e. on every cells-map change while a range is selected — and
   * it answers `false` at the first cell that is not fully marked, which is
   * almost always the first cell. Reading a long-text cell's string is O(text)
   * and a selection can be thousands of cells; materializing them all just to
   * look at one would put that whole cost behind every keystroke on the sheet.
   * `toggleCellsMark` spreads it, since a press walks the list twice anyway.
   */
  private *markTargets(
    sheetId: string,
    cells: readonly { rowId: string; colId: string }[],
    kind: TextMarkKind,
  ): Generator<CellMarkTarget & { rowId: string; colId: string }> {
    for (const { rowId, colId } of cells) {
      yield {
        rowId,
        colId,
        text: this.board.getCellText(sheetId, rowId, colId),
        marks: this.board.getCellMarks(sheetId, rowId, colId, kind),
      };
    }
  }

  /* ------------------------------------------------------------------ */
  /* Floating elements (F-30/F-32/F-36) — per-property writes only (C2)   */
  /* ------------------------------------------------------------------ */

  /**
   * F-30 (images are ALWAYS elements now): create an image element. Omit
   * `anchor` (pointer over empty canvas) → a board-anchored floating element
   * at `(x, y)`; pass a `{kind:'cell',rowId,colId,dx,dy}` anchor (pointer over
   * a grid cell) → a CONTAINED element (the cell grows to fit it, F-26/F-36).
   * `x`/`y` are an absolute-position cache — for a contained element the render
   * layer derives the real position from the resolved cell rect + `(dx, dy)`.
   * Returns the new element id, or null if editing is frozen.
   */
  createImageElement(
    x: number,
    y: number,
    w: number,
    h: number,
    payload: Omit<ImageElementPayload, "kind">,
    anchor?: Anchor,
  ): string | null {
    if (!this.canEdit()) return null;
    const elementId = `e${newId(10)}`;
    const sheetId = this.store.getState().sheetId;
    // F-26/F-36 expand-only ratchet: create the element AND grow its cell in ONE
    // transaction (one sync unit, one undo step) so a contained image is never
    // clipped on the render right after it lands (docs/04 §5).
    this.board.transact(() => {
      this.board.createImageElement(elementId, x, y, w, h, payload, LOCAL_ORIGIN, anchor, this.user.id);
      if (anchor?.kind === "cell" && sheetId !== null) {
        this.board.ratchetCellToElements(sheetId, anchor.rowId, anchor.colId, LOCAL_ORIGIN);
      }
    }, LOCAL_ORIGIN);
    return elementId;
  }

  /**
   * F-135: create a `file` element — any uploaded file that is not a board
   * image. Same placement rule as an image (`createImageElement`): omit
   * `anchor` → a board-anchored floating card at `(x, y)`; a `{kind:"cell",…}`
   * anchor → a CONTAINED card whose cell grows to fit it (F-26/F-36), ratcheted
   * in the SAME transaction. Returns the new element id, or null while editing
   * is frozen (F-45).
   */
  createFileElement(
    x: number,
    y: number,
    w: number,
    h: number,
    payload: Omit<FileElementPayload, "kind">,
    anchor?: Anchor,
  ): string | null {
    if (!this.canEdit()) return null;
    const elementId = `e${newId(10)}`;
    const sheetId = this.store.getState().sheetId;
    this.board.transact(() => {
      this.board.createFileElement(elementId, x, y, w, h, payload, LOCAL_ORIGIN, anchor, this.user.id);
      if (anchor?.kind === "cell" && sheetId !== null) {
        this.board.ratchetCellToElements(sheetId, anchor.rowId, anchor.colId, LOCAL_ORIGIN);
      }
    }, LOCAL_ORIGIN);
    return elementId;
  }

  /**
   * F-31 (link cards are ALWAYS elements): create a skeleton link-card element
   * — `payload` is `{ url, domain?, status:"loading" }`. Omit `anchor` (paste
   * over empty canvas / no cell selection) → a board-anchored floating card at
   * `(x, y)`; pass a `{kind:"cell",…}` anchor (paste with a cell selected) → a
   * CONTAINED card whose cell grows to fit it (F-26/F-36), ratcheted in the SAME
   * transaction as the create. The async unfurl later patches the payload via
   * `setElementPayload` (UNFURL_ORIGIN, off the undo stack). Returns the new
   * element id, or null if editing is frozen (F-45).
   */
  createLinkCardElement(
    x: number,
    y: number,
    w: number,
    h: number,
    payload: Omit<LinkCardElementPayload, "kind">,
    anchor?: Anchor,
  ): string | null {
    if (!this.canEdit()) return null;
    const elementId = `e${newId(10)}`;
    const sheetId = this.store.getState().sheetId;
    this.board.transact(() => {
      this.board.createLinkCardElement(elementId, x, y, w, h, payload, LOCAL_ORIGIN, anchor, this.user.id);
      if (anchor?.kind === "cell" && sheetId !== null) {
        this.board.ratchetCellToElements(sheetId, anchor.rowId, anchor.colId, LOCAL_ORIGIN);
      }
    }, LOCAL_ORIGIN);
    return elementId;
  }

  /**
   * Freehand drawing: create a `drawing` element from one captured stroke. Omit
   * `anchor` (stroke started over empty canvas) → board-anchored at `(x, y)`;
   * pass a `{kind:"cell",…}` anchor (stroke started over a grid cell) → the
   * drawing FOLLOWS that cell. Unlike an image/link-card there is NO ratchet —
   * a drawing overflows cell lines freely and must never grow its cell
   * (`elementConfinesToCell` is false for drawings). Returns the new element id,
   * or null if editing is frozen (F-45).
   */
  createDrawingElement(
    x: number,
    y: number,
    w: number,
    h: number,
    payload: Omit<DrawingElementPayload, "kind">,
    anchor?: Anchor,
  ): string | null {
    if (!this.canEdit()) return null;
    const elementId = `e${newId(10)}`;
    this.board.createDrawingElement(elementId, x, y, w, h, payload, LOCAL_ORIGIN, anchor, this.user.id);
    return elementId;
  }

  /**
   * F-31 unfurl patch: replace an element's whole-object `payload` (loading →
   * ready/error). Uses UNFURL_ORIGIN so it stays OFF the user's undo stack (see
   * the constant). Deliberately NOT gated on `canEdit`: the async result may
   * land during a brief disconnect, and the skeleton must ALWAYS resolve to
   * ready/error rather than spin forever — it is one small LWW write, and the
   * accessor no-ops if the element was deleted/undone in the meantime.
   */
  setElementPayload(elementId: string, payload: ElementPayload): void {
    if (this.destroyed) return;
    this.board.setElementPayload(elementId, payload, UNFURL_ORIGIN);
  }

  /**
   * F-138: set (or, with undefined, clear) the clip range of a link card —
   * the stretch of the linked video this card stands for. The writer's own
   * edit: LOCAL_ORIGIN, so it is undoable and honours F-45 freezing, unlike
   * the unfurl's system patch above. The payload is re-read INSIDE the
   * transaction (C4): a card unfurled or deleted between the popover opening
   * and the click is judged as it is now, and a non-card is a no-op. Returns
   * false when nothing was written.
   */
  setLinkCardClip(elementId: string, range: ClipRange | undefined): boolean {
    if (!this.canEdit()) return false;
    return this.board.transact(() => {
      const current = this.board.getElement(elementId);
      if (current === undefined || current.payload.kind !== "linkCard") return false;
      this.board.setElementPayload(elementId, withClipRange(current.payload, range), LOCAL_ORIGIN);
      return true;
    }, LOCAL_ORIGIN);
  }

  /**
   * F-31 unfurl commit: patch the card's whole-object `payload` AND — F-31(a),
   * 2026-07-15 — resize it to the thumbnail's real aspect, in ONE transaction
   * (one sync unit: peers see the card resolve and take its shape together,
   * never a 16:9 box that reflows a frame later). UNFURL_ORIGIN keeps both off
   * the undo stack, like the payload patch always has been.
   *
   * `size` null (no thumbnail, or a size we could not trust — see
   * aspect.linkCardSizeForPayload) writes geometry not at all: the favicon /
   * domain-only rungs keep the skeleton box.
   *
   * The geometry write is GUARDED on the card still being its skeleton size
   * (`skeleton`), re-checked INSIDE the transaction. `w`/`h` are per-property
   * LWW keys (C2), so an unconditional write here would silently revert a
   * resize the user (or a peer) performed while the fetch was in flight — the
   * unfurl arrives seconds late and must never win that race. Unchanged size
   * ⇒ nobody touched it ⇒ ours is the only intent in play. Not gated on
   * `canEdit` for the same reason as `setElementPayload`: the skeleton must
   * always resolve.
   *
   * A CELL-ANCHORED card re-ratchets its cell in the same transaction (F-26/
   * F-36), exactly like every other path that changes a contained element's
   * size (create, drag/resize commit). This resize is not the user's — it is a
   * server result arriving seconds late — but the cell must fit it just the
   * same: without the ratchet, `containedElementRect` clamps the resolved card
   * to the old cell width (cropping the thumbnail at the wrong ratio) and the
   * new height spills over the rows below until someone happens to nudge it.
   */
  applyUnfurl(
    elementId: string,
    payload: ElementPayload,
    size: { w: number; h: number } | null,
    skeleton: { w: number; h: number },
  ): void {
    if (this.destroyed) return;
    const sheetId = this.store.getState().sheetId;
    this.board.transact(() => {
      // F-138: the patch replaces the whole payload key, so a clip range the
      // writer set while the card was still loading rides across on the
      // CURRENT payload, re-read inside the transaction — never the fetch's
      // stale idea of the card.
      const before = this.board.getElement(elementId)?.payload;
      const patched =
        payload.kind === "linkCard" && before?.kind === "linkCard"
          ? withClipRange(payload, clipRangeOf(before))
          : payload;
      this.board.setElementPayload(elementId, patched, UNFURL_ORIGIN);
      if (size === null) return;
      const current = this.board.getElement(elementId);
      if (current === undefined) return; // deleted / undone mid-fetch (C4)
      if (current.w !== skeleton.w || current.h !== skeleton.h) return; // resized meanwhile
      this.board.setElementGeometry(elementId, { w: size.w, h: size.h }, UNFURL_ORIGIN);
      // After the geometry write, so the ratchet reads the card's NEW extent.
      if (current.anchor.kind === "cell" && sheetId !== null) {
        this.board.ratchetCellToElements(
          sheetId,
          current.anchor.rowId,
          current.anchor.colId,
          UNFURL_ORIGIN,
        );
      }
    }, UNFURL_ORIGIN);
  }

  /**
   * F-32/F-36 drop-time fit: READJUST a cell's stored row height / column
   * width to the elements its row/column contains after a drag-drop — grows so
   * the dropped element is never clipped, shrinks back (floored at the
   * defaults) once an element left or got smaller. MUST be called INSIDE an
   * open session transaction (`batch` from the drag/resize commit), AFTER the
   * anchor/geometry writes it reacts to, so the readjustment reads the
   * element's new home and ships as one undo step / sync unit with the
   * mutation, carrying the SAME LOCAL_ORIGIN. No-op if the active sheet is
   * unknown or the cell was deleted concurrently (C4).
   */
  fitCell(rowId: string, colId: string): void {
    const sheetId = this.store.getState().sheetId;
    if (sheetId === null) return;
    this.board.fitCellToElements(sheetId, rowId, colId, LOCAL_ORIGIN);
  }

  /** F-32 move: writes only `x`/`y` — merges with a concurrent resize (C2). */
  moveElement(elementId: string, x: number, y: number): void {
    if (!this.canEdit()) return;
    this.board.moveElement(elementId, x, y, LOCAL_ORIGIN);
  }

  /**
   * F-32 drag/resize commit: writes ONLY the geometry keys the interaction
   * actually changed — each its own LWW key (C2). Re-writing an unchanged
   * key (e.g. x/y on a SE-corner resize) would revert a concurrent remote
   * move of the same element, the exact move-vs-resize lost update the
   * per-property layout exists to prevent. One transaction = one undo step.
   */
  setElementRect(elementId: string, changes: ElementGeometryPatch): void {
    if (!this.canEdit()) return;
    this.board.setElementGeometry(elementId, changes, LOCAL_ORIGIN);
  }

  /**
   * F-32/F-36 re-anchor: replace an element's whole-object `anchor` (C2 — one
   * key for the reference+offset). This is the ONE write for every re-home —
   * board↔cell, cell→another cell, and moving a CONTAINED element by its
   * offset within the same cell (new dx/dy). One undo step; concurrent
   * re-anchors LWW-converge. (moveElement/setElementRect only touch x/y/w/h,
   * which a contained element's render ignores.)
   */
  setElementAnchor(elementId: string, anchor: Anchor): void {
    if (!this.canEdit()) return;
    this.board.setElementAnchor(elementId, anchor, LOCAL_ORIGIN);
  }

  /**
   * docs/07 C4 / docs/04 §2 element degradation (F-36 analog of comment
   * degradation): a CONTAINED element whose anchor cell (row or column) was
   * deleted degrades to a BOARD anchor at its last resolved board position,
   * instead of persisting forever as a zombie pinned to a dead cell (rendering
   * from a stale `x`/`y` cache — the origin for a migrated image). Writes the
   * whole-object anchor AND the `x`/`y` cache the board render reads, in ONE
   * transaction. ELEMENT_DEGRADE_ORIGIN keeps it off the undo stack (system
   * consequence, not a user edit). Idempotent + re-checked: a no-op if the
   * element is gone or already board-anchored (a racing client won).
   */
  degradeElementToBoard(elementId: string, position: { x: number; y: number }): void {
    if (!this.canEdit()) return;
    const element = this.board.getElement(elementId);
    if (element === undefined || element.anchor.kind !== "cell") return;
    const x = Math.round(position.x);
    const y = Math.round(position.y);
    this.board.transact(() => {
      this.board.setElementAnchor(elementId, { kind: "board", x, y }, ELEMENT_DEGRADE_ORIGIN);
      this.board.setElementGeometry(elementId, { x, y }, ELEMENT_DEGRADE_ORIGIN);
    }, ELEMENT_DEGRADE_ORIGIN);
  }

  /** F-32 z-order: single LWW `z` write; render ties break by (z, id). */
  bringElementToFront(elementId: string): void {
    if (!this.canEdit()) return;
    const zs = this.board.listElements().map((e) => e.z);
    this.board.setElementZ(elementId, (zs.length > 0 ? Math.max(...zs) : 0) + 1, LOCAL_ORIGIN);
  }

  sendElementToBack(elementId: string): void {
    if (!this.canEdit()) return;
    const zs = this.board.listElements().map((e) => e.z);
    this.board.setElementZ(elementId, (zs.length > 0 ? Math.min(...zs) : 0) - 1, LOCAL_ORIGIN);
  }

  /**
   * Delete an element AND cascade the F-38 connectors attached to it, in ONE
   * transaction: one sync unit, one undo step. Undo therefore restores the
   * element WITH its lines — the same "a delete takes its dependents with it,
   * an undo brings them back" rule comment threads follow on re-anchor
   * (docs/04 §3), and the reason no peer ever renders a line into a hole.
   */
  deleteElement(elementId: string): void {
    this.deleteElements([elementId]);
  }

  /**
   * F-34: delete a whole multi-selection as ONE undoable step (the Del key on
   * a marquee selection). Cascades each element's connectors like the single
   * delete, and lines BETWEEN two deleted elements are removed once — the
   * cascade re-reads the doc, so it never trips over an already-deleted id.
   */
  deleteElements(elementIds: readonly string[]): void {
    if (!this.canEdit() || elementIds.length === 0) return;
    this.board.transact(() => {
      for (const elementId of elementIds) {
        this.board.deleteConnectorsForElement(elementId, LOCAL_ORIGIN);
        this.board.deleteElement(elementId, LOCAL_ORIGIN);
      }
    }, LOCAL_ORIGIN);
  }

  /* ------------------------------------------------------------------ */
  /* Connectors (F-38)                                                   */
  /* ------------------------------------------------------------------ */

  /**
   * F-38: create a connector between two element ports. Returns the new id, or
   * null while editing is frozen (F-45). Guarded on both elements still
   * existing at write time — a drag can outlive a peer's delete of its target
   * (C4), and a line into a hole must never be created rather than swept later.
   */
  createConnector(from: ConnectorEndpoint, to: ConnectorEndpoint): string | null {
    if (!this.canEdit()) return null;
    if (!this.board.hasElement(from.elementId) || !this.board.hasElement(to.elementId)) return null;
    const connectorId = `k${newId(10)}`;
    this.board.addConnector(connectorId, from, to, LOCAL_ORIGIN);
    return connectorId;
  }

  deleteConnector(connectorId: string): void {
    if (!this.canEdit()) return;
    this.board.deleteConnector(connectorId, LOCAL_ORIGIN);
  }

  /**
   * C4 sweep for connectors left dangling by a delete this client did not
   * perform the cascade for — a line drawn concurrently with (or synced after)
   * the removal of an element it lands on. Runs under CONNECTOR_SWEEP_ORIGIN so
   * it stays OFF the user's undo stack: it is a system consequence of someone
   * else's delete, not this user's edit. Idempotent and re-checked inside the
   * transaction, so every client observing the same dangling line converges.
   */
  sweepConnectors(): void {
    if (!this.canEdit()) return;
    this.board.sweepConnectors(CONNECTOR_SWEEP_ORIGIN);
  }

  /* ------------------------------------------------------------------ */
  /* Multi-element move / duplicate (F-34)                               */
  /* ------------------------------------------------------------------ */

  /**
   * F-32/F-34 drag commit for one OR many elements: every plan's anchor and
   * geometry writes, plus the cell re-fits they imply, land in ONE transaction
   * = one sync unit = one undo step. A group drag must be undone as the one
   * gesture it was, and the cell fits must read the elements' NEW homes, which
   * is why the fits run last inside the same transaction (`fitCell`'s contract).
   *
   * Each plan carries ONLY the keys its element actually changed (C2) — see
   * `planElementMove`, which is where that decision is made and tested.
   */
  applyElementMoves(plans: readonly ElementMovePlan[]): void {
    if (!this.canEdit() || plans.length === 0) return;
    this.batch(() => {
      for (const plan of plans) {
        if (plan.anchor !== undefined) this.setElementAnchor(plan.elementId, plan.anchor);
        if (Object.keys(plan.patch).length > 0) this.setElementRect(plan.elementId, plan.patch);
      }
      // After every anchor/geometry write, so each fit sees the final layout —
      // two elements landing in the same cell must not size it from a half-
      // applied state.
      for (const plan of plans) {
        for (const cell of plan.fitCells) this.fitCell(cell.rowId, cell.colId);
      }
    });
  }

  /**
   * F-34 paste: create duplicates of copied elements (and the connectors that
   * ran between them) in ONE transaction — one undo step for the whole paste.
   * Fresh ids are minted here; `ref` is the clipboard's ORIGINAL id, used only
   * to rewire the copied connectors onto the copies. Returns the new element
   * ids in input order, so the caller can select what it just pasted.
   *
   * `z` walks up from the current top, preserving the copies' relative
   * stacking and putting the whole paste in front — the same rule as any
   * freshly-created element (`createImageElement`).
   */
  duplicateElements(
    elements: readonly ElementDuplicate[],
    connectors: readonly ConnectorDuplicate[],
  ): string[] {
    if (!this.canEdit() || elements.length === 0) return [];
    const sheetId = this.store.getState().sheetId;
    const idByRef = new Map<string, string>();
    const created: string[] = [];
    for (const element of elements) {
      const id = `e${newId(10)}`;
      idByRef.set(element.ref, id);
      created.push(id);
    }
    this.board.transact(() => {
      const baseZ = this.board.topZ() + 1;
      elements.forEach((element, index) => {
        const id = created[index];
        if (id === undefined) return;
        this.board.addElement(
          id,
          {
            type: element.type,
            x: element.x,
            y: element.y,
            w: element.w,
            h: element.h,
            z: baseZ + index,
            rotation: element.rotation,
            anchor: element.anchor,
            payload: element.payload,
            by: this.user.id,
          },
          LOCAL_ORIGIN,
        );
      });
      for (const connector of connectors) {
        const from = idByRef.get(connector.from.ref);
        const to = idByRef.get(connector.to.ref);
        if (from === undefined || to === undefined) continue;
        this.board.addConnector(
          `k${newId(10)}`,
          { elementId: from, side: connector.from.side },
          { elementId: to, side: connector.to.side },
          LOCAL_ORIGIN,
        );
      }
      // F-26/F-36: a duplicate landing in a cell grows it, exactly like the
      // original did on create — after the adds, so the ratchet sees them.
      if (sheetId !== null) {
        for (const element of elements) {
          if (element.anchor.kind !== "cell") continue;
          this.board.ratchetCellToElements(
            sheetId,
            element.anchor.rowId,
            element.anchor.colId,
            LOCAL_ORIGIN,
          );
        }
      }
    }, LOCAL_ORIGIN);
    return created;
  }

  /* ------------------------------------------------------------------ */
  /* Comments (F-50..F-55) — LOCAL_ORIGIN user ops, C4 degradation       */
  /* ------------------------------------------------------------------ */

  /**
   * F-50: start a thread with its first message on the given anchor
   * (hit-tested top-down by the caller — docs/04 §3). One transaction = one
   * sync unit = one undo step (F-43: comment ops are plainly undoable).
   * Returns the new thread id, or null while editing is frozen (F-45).
   *
   * `orphaned: true` covers the C4 composer race (docs/07 C4, docs/04 §3):
   * the target the composer was opened on was deleted before submit — the
   * caller falls back to a board anchor at the draft pin position and the
   * thread is created already flagged for the panel, in the SAME transaction
   * (still one undo step).
   */
  createCommentThread(
    anchor: Anchor,
    body: string,
    options?: { orphaned?: boolean },
  ): string | null {
    if (!this.canEdit()) return null;
    const threadId = `t${newId(10)}`;
    this.board.transact(() => {
      this.board.createThread(threadId, anchor, this.newCommentMessage(body), LOCAL_ORIGIN);
      if (options?.orphaned === true) this.board.markOrphaned(threadId, LOCAL_ORIGIN);
    }, LOCAL_ORIGIN);
    return threadId;
  }

  /** F-51 reply. False when the thread is gone (delete won — C4 style). */
  addCommentMessage(threadId: string, body: string): boolean {
    if (!this.canEdit()) return false;
    return this.board.addMessage(threadId, this.newCommentMessage(body), LOCAL_ORIGIN);
  }

  /** F-51 edit own message (the UI gates on authorship; CRDT does not). */
  editCommentMessage(threadId: string, messageId: string, body: string): boolean {
    if (!this.canEdit()) return false;
    return this.board.editMessage(threadId, messageId, body, Date.now(), LOCAL_ORIGIN);
  }

  /** F-51 delete own message; deleting the last message deletes the thread. */
  deleteCommentMessage(threadId: string, messageId: string): boolean {
    if (!this.canEdit()) return false;
    return this.board.deleteMessage(threadId, messageId, LOCAL_ORIGIN);
  }

  /**
   * F-52b: re-anchor a thread because the USER dragged its pin onto a
   * different element / cell / bare canvas. ONE whole-object `anchor` write
   * (C2 — the anchor is a single semantic unit), LOCAL_ORIGIN so it is plainly
   * undoable like any other edit this user made (F-43), and it never reads or
   * writes `messages` — a concurrent reply merges untouched.
   *
   * The caller decides the drop actually changed the target
   * (`anchorDropChanged`): dropping on the current target must not write.
   * Returns false when the thread is gone (deleted concurrently — C4).
   */
  setCommentThreadAnchor(threadId: string, anchor: Anchor): boolean {
    if (!this.canEdit()) return false;
    return this.board.setThreadAnchor(threadId, anchor, LOCAL_ORIGIN);
  }

  /** F-53 resolve / re-open — single boolean LWW, merges with replies. */
  setCommentThreadResolved(threadId: string, resolved: boolean): void {
    if (!this.canEdit()) return;
    this.board.setResolved(threadId, resolved, LOCAL_ORIGIN);
  }

  /**
   * docs/07 C4 / docs/04 §3 degradation: the anchor target was deleted →
   * re-anchor to the board at the pin's LAST RESOLVED position and flag the
   * thread orphaned, as ONE transaction (one sync unit). DEGRADE_ORIGIN:
   * never on the user's undo stack (see the constant's rationale). Both
   * writes are idempotent/LWW, so any number of clients observing the same
   * deletion converge.
   */
  orphanCommentThread(threadId: string, position: { x: number; y: number }): void {
    if (!this.canEdit()) return;
    this.board.transact(() => {
      this.board.reanchorToBoard(threadId, position, DEGRADE_ORIGIN);
      this.board.markOrphaned(threadId, DEGRADE_ORIGIN);
    }, DEGRADE_ORIGIN);
  }

  /**
   * C4 degradation, position-less variant: the anchor target is gone but NO
   * client ever resolved a pin position for the thread (target deleted
   * before any layout pass saw them coexist — the composer/creation race), so
   * there is no coordinate to re-anchor to. Flag the thread orphaned for the
   * panel (F-52 accessibility); the anchor keeps its dead target ids, which
   * doubles as the re-attach reference if the deletion is undone. Idempotent,
   * DEGRADE_ORIGIN (system consequence — never on the undo stack).
   */
  flagCommentThreadOrphaned(threadId: string): void {
    if (!this.canEdit()) return;
    this.board.markOrphaned(threadId, DEGRADE_ORIGIN);
  }

  /**
   * Reverse of the C4 degradation (docs/04 §3, amended 2026-07-13): the
   * thread's ORIGINAL anchor target exists again — its deletion was undone —
   * so restore the recorded anchor and clear the orphan flag. Callers verify
   * existence first (pins layer, against the same inputs it renders from).
   * DEGRADE_ORIGIN for the same reason as the degradation itself: every
   * observing client races the same idempotent write; it is nobody's edit.
   */
  restoreCommentAnchor(threadId: string): void {
    if (!this.canEdit()) return;
    this.board.restoreAnchor(threadId, DEGRADE_ORIGIN);
  }

  /**
   * Sweep one EMPTY comment thread (concurrent deletes of its last two
   * messages merged to zero — see BoardDoc.deleteThreadIfEmpty). Re-checked
   * inside the transaction; idempotent across racing clients; SWEEP_ORIGIN
   * keeps it off the undo stack.
   */
  deleteEmptyCommentThread(threadId: string): void {
    if (!this.canEdit()) return;
    this.board.deleteThreadIfEmpty(threadId, SWEEP_ORIGIN);
  }

  /** Stamp a new message with this session's identity (F-51 author). */
  private newCommentMessage(body: string): CommentMessage {
    return {
      id: `m${newId(10)}`,
      authorId: this.user.id,
      authorName: this.user.name,
      body,
      ts: Date.now(),
    };
  }

  /* ------------------------------------------------------------------ */
  /* Fine-grained cell-change notifications (grid damage path)           */
  /* ------------------------------------------------------------------ */

  /** Subscribe to changed cell keys ("rowId:colId") of the ACTIVE sheet. */
  onCellsChanged(listener: CellsChangedListener): () => void {
    this.cellListeners.add(listener);
    return () => {
      this.cellListeners.delete(listener);
    };
  }

  destroy(): void {
    setDebugConnection(null);
    if (this.destroyed) return;
    this.destroyed = true;
    this.clearOfflineTimer();
    this.detachSheetObservers?.();
    this.detachSheetObservers = null;
    getSheets(this.board.doc).unobserveDeep(this.sheetsObserver);
    getElements(this.board.doc).unobserveDeep(this.elementsObserver);
    getConnectors(this.board.doc).unobserveDeep(this.connectorsObserver);
    getComments(this.board.doc).unobserveDeep(this.commentsObserver);
    getDesign(this.board.doc).unobserve(this.designObserver);
    getDesignRefs(this.board.doc).unobserveDeep(this.designCollectionsObserver);
    getDesignShots(this.board.doc).unobserveDeep(this.designCollectionsObserver);
    getThumbs(this.board.doc).unobserve(this.thumbsObserver);
    getThumbPortraits(this.board.doc).unobserveDeep(this.thumbsCollectionsObserver);
    getThumbShots(this.board.doc).unobserveDeep(this.thumbsCollectionsObserver);
    this.undoManager.destroy();
    this.cellListeners.clear();
    this.provider.destroy();
    this.socket.destroy();
    this.board.doc.destroy();
  }

  /* ------------------------------------------------------------------ */
  /* Design theme                                                        */
  /* ------------------------------------------------------------------ */

  /**
   * Apply a whole generated art direction under LOCAL_ORIGIN — ONE undo step
   * for the whole generation (the shared accessor opens one transaction).
   *
   * This is why the design route is stateless: the generation lands as the
   * requesting user's OWN edit, so it is attributable, undoable, and needs no
   * C5 server-writer guard. Returns false if editing is frozen (F-45).
   */
  applyArtDirection(artDirection: ArtDirection, model?: string): boolean {
    if (!this.canEdit()) return false;
    this.board.setArtDirection(artDirection, {
      at: Date.now(),
      origin: LOCAL_ORIGIN,
      ...(model === undefined ? {} : { model }),
    });
    return true;
  }

  /** Hand-edit one art-direction field (C2: its own key, its own write). */
  setArtDirectionField(field: ArtDirectionTextField, text: string): boolean {
    if (!this.canEdit()) return false;
    this.board.setArtDirectionField(field, text, { at: Date.now(), origin: LOCAL_ORIGIN });
    return true;
  }

  /** Replace the palette (whole-object write — a palette is chosen as a set). */
  setDesignPalette(swatches: readonly PaletteSwatch[]): boolean {
    if (!this.canEdit()) return false;
    this.board.setDesignPalette(swatches, { at: Date.now(), origin: LOCAL_ORIGIN });
    return true;
  }

  /** The user's standing brief for the generator. */
  setDesignBrief(brief: string): boolean {
    if (!this.canEdit()) return false;
    this.board.setDesignBrief(brief, LOCAL_ORIGIN);
    return true;
  }

  /**
   * Attach a style-reference frame (an already-uploaded asset). Returns the
   * new ref id, or null if editing is frozen (F-45).
   */
  addDesignRef(assetId: string, w: number, h: number): string | null {
    if (!this.canEdit()) return null;
    const refId = `dr${newId(10)}`;
    this.board.addDesignRef(refId, { assetId, w, h }, LOCAL_ORIGIN);
    return refId;
  }

  setDesignRefNote(refId: string, note: string): boolean {
    if (!this.canEdit()) return false;
    this.board.setDesignRefNote(refId, note, LOCAL_ORIGIN);
    return true;
  }

  deleteDesignRef(refId: string): boolean {
    if (!this.canEdit()) return false;
    this.board.deleteDesignRef(refId, LOCAL_ORIGIN);
    return true;
  }

  /* ------------------------------------------------------------------ */
  /* Thumbnails (F-130)                                                  */
  /* ------------------------------------------------------------------ */

  /** The words on the thumbnails — the user's, each its own key (C2). */
  setThumbTitle(title: string): boolean {
    if (!this.canEdit()) return false;
    this.board.setThumbTitle(title, LOCAL_ORIGIN);
    return true;
  }

  setThumbText(text: string): boolean {
    if (!this.canEdit()) return false;
    this.board.setThumbText(text, LOCAL_ORIGIN);
    return true;
  }

  /** Board-wide model picks; `undefined` = the server's default. */
  setThumbImageModel(model: string | undefined): boolean {
    if (!this.canEdit()) return false;
    this.board.setThumbImageModel(model, LOCAL_ORIGIN);
    return true;
  }

  setThumbTextModel(model: string | undefined): boolean {
    if (!this.canEdit()) return false;
    this.board.setThumbTextModel(model, LOCAL_ORIGIN);
    return true;
  }

  /**
   * Attach a portrait (an already-uploaded asset) at the end of the list.
   * Returns the new id, or null if editing is frozen (F-45).
   */
  addThumbPortrait(assetId: string, w: number, h: number, label?: string): string | null {
    if (!this.canEdit()) return null;
    const portraitId = `tp${newId(10)}`;
    this.board.addThumbPortrait(
      portraitId,
      { assetId, w, h, ...(label === undefined ? {} : { label }) },
      LOCAL_ORIGIN,
    );
    return portraitId;
  }

  setThumbPortraitLabel(portraitId: string, label: string): boolean {
    if (!this.canEdit()) return false;
    this.board.setThumbPortraitLabel(portraitId, label, LOCAL_ORIGIN);
    return true;
  }

  /** Make this portrait the presenter (first in order). */
  makeThumbPresenter(portraitId: string): boolean {
    if (!this.canEdit()) return false;
    this.board.moveThumbPortraitToFront(portraitId, LOCAL_ORIGIN);
    return true;
  }

  deleteThumbPortrait(portraitId: string): boolean {
    if (!this.canEdit()) return false;
    this.board.deleteThumbPortrait(portraitId, LOCAL_ORIGIN);
    return true;
  }

  /** Mark one thumbnail as the team's pick (or clear the pick). */
  selectThumb(shotId: string | undefined): boolean {
    if (!this.canEdit()) return false;
    this.board.setSelectedThumb(shotId, LOCAL_ORIGIN);
    return true;
  }

  /**
   * Put a generated thumbnail on the board as a real, draggable image element
   * — and make it the pick — in ONE transaction (one sync unit, one undo
   * step). Returns the element id, or null if editing is frozen.
   */
  placeThumbOnBoard(
    shot: ThumbShotInfo,
    rect: { x: number; y: number; w: number; h: number },
  ): string | null {
    if (!this.canEdit()) return null;
    const elementId = `e${newId(10)}`;
    this.board.transact(() => {
      this.board.createImageElement(
        elementId,
        rect.x,
        rect.y,
        rect.w,
        rect.h,
        { assetId: shot.assetId, w: shot.w, h: shot.h },
        LOCAL_ORIGIN,
        undefined,
        this.user.id,
      );
      this.board.setThumbShotElement(shot.id, elementId, LOCAL_ORIGIN);
      this.board.setSelectedThumb(shot.id, LOCAL_ORIGIN);
    }, LOCAL_ORIGIN);
    return elementId;
  }

  /* ------------------------------------------------------------------ */
  /* Internals                                                           */
  /* ------------------------------------------------------------------ */

  private canEdit(): boolean {
    return !this.destroyed && !this.store.getState().frozen;
  }

  private handleStatus(status: WebSocketStatus): void {
    if (this.destroyed) return;
    const mapped: ConnectionStatus =
      status === WebSocketStatus.Connected
        ? "connected"
        : status === WebSocketStatus.Connecting
          ? "connecting"
          : "disconnected";
    this.store.setState({ status: mapped });
    setDebugConnection(mapped);
    if (mapped === "connected") {
      debugLog.info("socket", "Connected");
      this.clearOfflineTimer();
      this.store.setState({ frozen: false });
    } else {
      if (mapped === "disconnected") debugLog.warn("socket", "Connection lost — reconnecting");
      this.startOfflineTimer();
    }
  }

  private handleSynced(state: boolean): void {
    if (this.destroyed) return;
    if (state) {
      // First-open seeding happens SERVER-SIDE in onLoadDocument (single
      // serialized writer) — by the time this first sync completes, a brand
      // new board already carries its default sheet. Clients never bootstrap:
      // two clients racing a client-side bootstrap would lazily create the
      // same nested meta entry maps, and edits made into the losing entry
      // before merge are silently discarded (the C2 subtree failure mode).
      this.refreshSheets();
    }
    this.store.setState({ synced: state });
  }

  /** Keep the active sheet valid; (re)wire per-sheet observers + undo. */
  private refreshSheets(): void {
    if (this.destroyed) return;
    const sheets = this.board.listSheets();
    const current = this.store.getState().sheetId;
    const next =
      current !== null && sheets.some((s) => s.id === current) ? current : (sheets[0]?.id ?? null);
    if (next !== current) {
      this.setActiveSheet(next);
    } else {
      this.bumpStructure();
    }
  }

  /** True for transactions NOT made by this user (remote peers, server). */
  private isForeignOrigin(origin: unknown): boolean {
    return origin !== LOCAL_ORIGIN && origin !== this.undoManager;
  }

  /**
   * C6-for-threads index (see undo()): record which threads a FOREIGN
   * transaction has engaged with — replies/edits/deletes inside the messages
   * array, or a resolve/re-open. The C4 degradation keys (`anchor`,
   * `orphaned`, `origAnchor`) are deliberately excluded: a system degradation
   * (any origin, including this client's own DEGRADE_ORIGIN) is not foreign
   * ENGAGEMENT and must not permanently block undoing a thread creation.
   */
  private trackForeignCommentEdits(
    events: Y.YEvent<Y.Map<unknown>>[],
    transaction: Y.Transaction,
  ): void {
    if (!this.isForeignOrigin(transaction.origin)) return;
    for (const event of events) {
      if (event.path.length === 0) continue; // thread create/delete — not an in-thread edit
      const threadId = String(event.path[0]);
      if (event.path.length >= 2) {
        // Deep event inside the thread entry — the messages Y.Array.
        if (event.path[1] === "messages") this.foreignTouchedThreadIds.add(threadId);
        continue;
      }
      // Key writes on the thread entry map itself.
      for (const key of event.changes.keys.keys()) {
        if (key === "messages" || key === "resolved") {
          this.foreignTouchedThreadIds.add(threadId);
        }
      }
    }
  }

  private setActiveSheet(sheetId: string | null): void {
    const previousSheetId = this.store.getState().sheetId;
    this.detachSheetObservers?.();
    this.detachSheetObservers = null;
    // Leaving an ACTIVE sheet invalidates its history: the C6/C8 guard state
    // below is reset, so items captured against the old sheet must not be
    // re-appliable (same semantics as the previous destroy-and-recreate).
    // The initial null → first-sheet transition deliberately does NOT clear:
    // element edits made before the first sync stay undoable (see the
    // constructor note on the session-lifetime UndoManager).
    if (previousSheetId !== null) this.undoManager.clear();
    this.foreignCellWrites = new Set();
    this.foreignTouchedRowIds = new Set();
    this.foreignTouchedColIds = new Set();
    this.undoItemInfo = new WeakMap();

    if (sheetId !== null) {
      const doc = this.board.doc;
      const cells = getCells(doc, sheetId);
      const rowMeta = getRowMeta(doc, sheetId);
      const colMeta = getColMeta(doc, sheetId);

      // Fine-grained damage: collect changed cell ADDRESSES ("rowId:colId")
      // from deep events. Storage keys are per-property ("rowId:colId:prop",
      // C2 flat keys) — normalize them; a Y.Text `v` edit surfaces as a deep
      // event whose path[0] is the property key. The same pass maintains the
      // C8 last-writer index: a key is "foreign-owned" when the most recent
      // transaction touching it did not come from this user.
      const cellsObserver = (
        events: Y.YEvent<Y.Map<unknown>>[],
        transaction: Y.Transaction,
      ) => {
        const foreign = this.isForeignOrigin(transaction.origin);
        const keys = new Set<string>();
        for (const event of events) {
          const rawKeys =
            event.path.length === 0 ? event.changes.keys.keys() : [String(event.path[0])];
          for (const raw of rawKeys) {
            const parsed = parseCellPropKey(raw);
            keys.add(parsed ? cellKey(parsed.rowId, parsed.colId) : raw);
            if (foreign) {
              this.foreignCellWrites.add(raw);
              if (parsed) {
                // C6 index: foreign content in these rows/cols blocks
                // structural undo of the op that created them.
                this.foreignTouchedRowIds.add(parsed.rowId);
                this.foreignTouchedColIds.add(parsed.colId);
              }
            } else {
              this.foreignCellWrites.delete(raw); // we are the last writer now
            }
          }
        }
        if (keys.size > 0) {
          const list = [...keys];
          for (const listener of this.cellListeners) listener(list);
        }
      };
      // Structure changes bump the layout version; foreign edits to a row's
      // or column's own meta (height, width, name, ord) also count as
      // "someone else edited the affected rows/columns" for C6.
      const rowMetaObserver = (
        events: Y.YEvent<Y.Map<unknown>>[],
        transaction: Y.Transaction,
      ) => {
        if (this.isForeignOrigin(transaction.origin)) {
          for (const event of events) {
            if (event.path.length > 0) this.foreignTouchedRowIds.add(String(event.path[0]));
          }
        }
        this.bumpStructure();
      };
      const colMetaObserver = (
        events: Y.YEvent<Y.Map<unknown>>[],
        transaction: Y.Transaction,
      ) => {
        if (this.isForeignOrigin(transaction.origin)) {
          for (const event of events) {
            if (event.path.length > 0) this.foreignTouchedColIds.add(String(event.path[0]));
          }
        }
        this.bumpStructure();
      };

      cells.observeDeep(cellsObserver);
      rowMeta.observeDeep(rowMetaObserver);
      colMeta.observeDeep(colMetaObserver);

      // The sheet's grid types join the session-lifetime UndoManager (created
      // in the constructor with the elements map, so pre-sync element edits
      // are already covered). One undo stack for grid + canvas; element items
      // carry no undoItemInfo, so the C6/C8 cell guards don't apply to them —
      // element races are the accepted C9 behavior. yjs has no
      // removeFromScope; stale scopes from a previous sheet are harmless
      // because its stack items were cleared above and its observers detached.
      const undoManager = this.undoManager;
      undoManager.addToScope([cells, rowMeta, colMeta]);

      // Stamp every UNDO stack item with what it touched (cell keys, created
      // row/col ids) so undo() can apply the C6/C8 guards before re-applying
      // it. 'stack-item-updated' fires when a transaction merges into the
      // previous item (captureTimeout) — union into the same record.
      const recordItemInfo = (event: StackItemEventLike) => {
        if (event.type !== "undo") return; // redo items are yjs-guarded
        let info = this.undoItemInfo.get(event.stackItem);
        if (!info) {
          info = {
            cellPropKeys: new Set(),
            createdRowIds: new Set(),
            createdColIds: new Set(),
          };
          this.undoItemInfo.set(event.stackItem, info);
        }
        for (const cellEvent of event.changedParentTypes.get(cells) ?? []) {
          const rawKeys =
            cellEvent.path.length === 0
              ? [...cellEvent.changes.keys.keys()]
              : [String(cellEvent.path[0])];
          for (const raw of rawKeys) info.cellPropKeys.add(raw);
        }
        for (const rowEvent of event.changedParentTypes.get(rowMeta) ?? []) {
          if (rowEvent.path.length !== 0) continue;
          for (const [key, change] of rowEvent.changes.keys) {
            if (change.action === "add") info.createdRowIds.add(key);
          }
        }
        for (const colEvent of event.changedParentTypes.get(colMeta) ?? []) {
          if (colEvent.path.length !== 0) continue;
          for (const [key, change] of colEvent.changes.keys) {
            if (change.action === "add") info.createdColIds.add(key);
          }
        }
      };
      undoManager.on("stack-item-added", recordItemInfo);
      undoManager.on("stack-item-updated", recordItemInfo);

      this.detachSheetObservers = () => {
        cells.unobserveDeep(cellsObserver);
        rowMeta.unobserveDeep(rowMetaObserver);
        colMeta.unobserveDeep(colMetaObserver);
        undoManager.off("stack-item-added", recordItemInfo);
        undoManager.off("stack-item-updated", recordItemInfo);
      };
    }

    this.store.setState((s) => ({ sheetId, structureVersion: s.structureVersion + 1 }));
  }

  private bumpStructure(): void {
    if (this.destroyed) return;
    this.store.setState((s) => ({ structureVersion: s.structureVersion + 1 }));
  }

  private bumpElements(): void {
    if (this.destroyed) return;
    this.store.setState((s) => ({ elementsVersion: s.elementsVersion + 1 }));
  }

  private bumpConnectors(): void {
    if (this.destroyed) return;
    this.store.setState((s) => ({ connectorsVersion: s.connectorsVersion + 1 }));
  }

  private bumpComments(): void {
    if (this.destroyed) return;
    this.store.setState((s) => ({ commentsVersion: s.commentsVersion + 1 }));
  }

  private bumpDesign(): void {
    if (this.destroyed) return;
    this.store.setState((s) => ({ designVersion: s.designVersion + 1 }));
  }

  private bumpThumbs(): void {
    if (this.destroyed) return;
    this.store.setState((s) => ({ thumbsVersion: s.thumbsVersion + 1 }));
  }

  private handleAwareness(states: { clientId: number; [key: string]: unknown }[]): void {
    if (this.destroyed) return;
    const selfId = this.provider.awareness?.clientID;
    const peers: RemotePeer[] = [];
    for (const state of states) {
      if (state.clientId === selfId) continue;
      const user = state["user"] as PresenceUser | undefined | null;
      if (!user) continue;
      const peer: RemotePeer = { clientId: state.clientId, user };
      const selection = state["selection"] as CellRangeSelection | undefined | null;
      if (selection) peer.selection = selection;
      const editingCell = state["editingCell"] as CellAddress | undefined | null;
      if (editingCell) peer.editingCell = editingCell;
      const grabbedElement = state["grabbedElement"] as GrabbedElement | undefined | null;
      if (grabbedElement) peer.grabbedElement = grabbedElement;
      peers.push(peer);
    }
    peers.sort((a, b) => a.clientId - b.clientId);
    this.store.setState({ peers });
  }

  private startOfflineTimer(): void {
    if (this.offlineTimer !== null || this.destroyed) return;
    this.offlineTimer = window.setTimeout(() => {
      this.offlineTimer = null;
      // F-45: offline > 60 s — block editing to bound divergence; reads stay.
      debugLog.error("socket", "Offline for more than 60 s — editing frozen until reconnect");
      this.store.setState({ frozen: true });
    }, OFFLINE_FREEZE_MS);
  }

  private clearOfflineTimer(): void {
    if (this.offlineTimer !== null) {
      window.clearTimeout(this.offlineTimer);
      this.offlineTimer = null;
    }
  }
}
